import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShoppingCart, Package } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';
import { MobileNav } from '../components/MobileNav';

const playfairFont = "'Playfair Display', serif";
const fmtIDR = (n) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(
    Math.max(0, Number(n) || 0),
  );

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState({}); // groupName -> chosen value
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products/${id}`);
        setProduct(data);
      } catch {
        toast.error('Produk tidak ditemukan');
        navigate('/');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080C0A' }}>
        <div className="w-12 h-12 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
      </div>
    );
  }
  if (!product) return null;

  const variants = product.variants || [];
  const hasVariants = variants.length > 0;

  // Resolve the selected option per group
  const chosenOpts = variants.map((g) => {
    const v = selection[g.name];
    return v ? (g.options || []).find((o) => o.value === v) : null;
  });
  const allRequiredSelected = variants.every((g, idx) => (g.required === false ? true : !!chosenOpts[idx]));

  // Effective price = base + sum of adjusts (or override wins if any)
  let effectivePrice = Number(product.price) || 0;
  let overrideApplied = false;
  let stockLeft = product.stock || 0;
  if (hasVariants && allRequiredSelected) {
    let acc = Number(product.price) || 0;
    let minStock = null;
    for (const opt of chosenOpts) {
      if (!opt) continue;
      if (opt.price_override != null && opt.price_override !== '') {
        acc = Number(opt.price_override);
        overrideApplied = true;
      } else {
        acc += Number(opt.price_adjust || 0);
      }
      const s = Number(opt.stock || 0);
      if (minStock === null || s < minStock) minStock = s;
    }
    effectivePrice = Math.max(0, acc);
    stockLeft = minStock ?? 0;
  }

  const handleAddToCart = async () => {
    if (hasVariants && !allRequiredSelected) {
      toast.error('Mohon pilih semua varian yang wajib');
      return;
    }
    if (stockLeft < quantity) {
      toast.error('Stok tidak cukup');
      return;
    }
    setAddingToCart(true);
    try {
      await axios.post(
        `${API}/cart/add`,
        {
          product_id: product.id,
          quantity,
          variant_selection: hasVariants ? selection : null,
        },
        { withCredentials: true },
      );
      toast.success('Ditambahkan ke keranjang!');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Silakan login terlebih dahulu');
        navigate('/login');
      } else {
        toast.error(err.response?.data?.detail || 'Gagal menambahkan ke keranjang');
      }
    } finally {
      setAddingToCart(false);
    }
  };

  const images = (product.images && product.images.length ? product.images : [product.image_url]).filter(Boolean);

  return (
    <div className="min-h-screen pb-32" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.85)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="hover:opacity-80" data-testid="pdp-back-btn">
            <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
          </button>
          <h1 className="text-lg font-light truncate" style={{ fontFamily: playfairFont, color: '#F8FAF9' }} data-testid="pdp-name">
            {product.name}
          </h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 grid md:grid-cols-2 gap-10">
        {/* Gallery */}
        <div className="space-y-3">
          <div className="aspect-square rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="pdp-main-image">
            <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {images.map((src, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImg(idx)}
                  className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0"
                  style={{ border: `1px solid ${idx === activeImg ? '#D4AF37' : 'rgba(255,255,255,0.1)'}` }}
                  data-testid={`pdp-thumb-${idx}`}
                >
                  <img src={src} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-6">
          <div>
            <h2 className="text-3xl md:text-4xl font-light mb-3" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>{product.name}</h2>
            <p className="text-sm" style={{ color: '#A3B8AC' }}>{product.category}</p>
          </div>

          <div>
            <p className="text-3xl font-semibold" style={{ color: '#D4AF37', fontFamily: playfairFont }} data-testid="pdp-price">
              {fmtIDR(effectivePrice)}
            </p>
            {!hasVariants && product.original_price && product.original_price > product.price && (
              <p className="text-sm line-through mt-1" style={{ color: '#A3B8AC' }}>{fmtIDR(product.original_price)}</p>
            )}
            {hasVariants && overrideApplied && (
              <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>Harga ditentukan dari varian terpilih.</p>
            )}
          </div>

          <p className="text-sm leading-relaxed" style={{ color: '#CFE0D5' }}>{product.description}</p>

          {/* Variants */}
          {variants.map((grp) => (
            <div key={grp.name} data-testid={`pdp-variant-group-${grp.name}`}>
              <p className="text-sm font-medium mb-2" style={{ color: '#F8FAF9' }}>
                {grp.name}
                {grp.required !== false && <span style={{ color: '#EF4444' }}> *</span>}
                {selection[grp.name] && <span className="ml-2 text-xs" style={{ color: '#D4AF37' }}>{selection[grp.name]}</span>}
              </p>
              <div className="flex flex-wrap gap-2">
                {(grp.options || []).map((opt) => {
                  const isSelected = selection[grp.name] === opt.value;
                  const soldOut = (opt.stock || 0) <= 0;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={soldOut}
                      onClick={() => {
                        setSelection((s) => ({ ...s, [grp.name]: opt.value }));
                        setQuantity(1);
                      }}
                      className="px-4 py-2 rounded-full text-sm transition-all"
                      style={{
                        background: isSelected ? '#D4AF37' : 'rgba(255,255,255,0.04)',
                        color: isSelected ? '#080C0A' : '#F8FAF9',
                        border: `1px solid ${isSelected ? '#D4AF37' : 'rgba(212,175,55,0.25)'}`,
                        opacity: soldOut ? 0.4 : 1,
                        cursor: soldOut ? 'not-allowed' : 'pointer',
                        textDecoration: soldOut ? 'line-through' : 'none',
                      }}
                      data-testid={`pdp-opt-${grp.name}-${opt.value}`}
                    >
                      {opt.value}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Stock */}
          <p className="text-sm flex items-center gap-2" style={{ color: stockLeft > 0 ? '#22C55E' : '#EF4444' }} data-testid="pdp-stock">
            <Package className="w-4 h-4" />
            {stockLeft > 0 ? `Stok tersedia: ${stockLeft}` : 'Stok habis'}
          </p>

          {/* Quantity */}
          {stockLeft > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm" style={{ color: '#A3B8AC' }}>Qty</span>
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="w-8 h-8 rounded-full"
                style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9' }}
                data-testid="pdp-qty-minus"
              >-</button>
              <span className="w-10 text-center" style={{ color: '#F8FAF9' }} data-testid="pdp-qty">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.min(stockLeft, q + 1))}
                className="w-8 h-8 rounded-full"
                style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9' }}
                data-testid="pdp-qty-plus"
              >+</button>
            </div>
          )}

          {/* CTA */}
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleAddToCart}
            disabled={addingToCart || stockLeft === 0 || (hasVariants && !allRequiredSelected)}
            className="w-full md:w-auto px-8 py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: '#D4AF37', color: '#080C0A', boxShadow: '0 12px 32px rgba(212,175,55,0.28)' }}
            data-testid="pdp-add-to-cart-btn"
          >
            <ShoppingCart className="w-4 h-4" />
            {hasVariants && !allRequiredSelected ? 'Pilih Varian Dulu' : 'Tambah ke Keranjang'}
          </motion.button>
        </div>
      </div>

      <MobileNav />
    </div>
  );
};
